import { useCallback, useMemo, useState } from 'react';
import type { TokenCliente } from '../api/ops/clienteFinal';
import { operacionesCompraEntradas } from '../api/ops/compraEntradas';
import { operacionesMisEntradas } from '../api/ops/misEntradas';
import { operacionesMisReservas } from '../api/ops/misReservas';
import { tokenCaptcha } from '../acceso/InvitacionCaptcha';
import { Chip } from '../componentes/Chip';
import { ComprarEntradas } from './ComprarEntradas';
import { Icono } from '../componentes/Icono';
import { esRutaPase, RUTAS_CLIENTE } from '../rutas';
import { crearAlmacenCliente, type AlmacenCliente, type SesionCliente } from './almacenCliente';
import { clavePublicaDelEntorno, crearApiPorDefecto, operacionesDeLaZona, type CrearApi } from './apiCliente';
import { PaginaBaja, PaginaCopia, PaginaPermiso } from './Enlaces';
import { Identificarse } from './Identificarse';
import { MisDatos } from './MisDatos';
import { MisEntradas } from './MisEntradas';
import { MisConversaciones } from './MisConversaciones';
import { MisMensajes } from './MisMensajes';
import { MisPagos } from './MisPagos';
import { MisPases } from './MisPases';
import { MisReservas } from './MisReservas';
import { MisDocumentos } from './MisDocumentos';
import { MisCitas } from './MisCitas';
import { EnlaceCita } from './EnlaceCita';
import { PedirCita } from './PedirCita';
import { PaginaFirma } from './PaginaFirma';
import { PaginaPago } from './PaginaPago';
import { PaginaPase } from './PaginaPase';
import { ReservaPublica } from './ReservaPublica';
import { Baldosa, Pagina } from './Pagina';
import { esPaginaEnMarco, useAlturaEnMarco } from './alturaEnMarco';
import { SalirDeLaLista } from './SalirDeLaLista';
import { D, T } from './textos';
import { idiomaInicial } from './textosEntradas';

/**
 * Zona de las **páginas del cliente** (G157, ADR-0058): rutas propias sin el marco del panel y con su sesión aparte (el
 * token de cliente en sessionStorage, nunca la sesión del equipo).
 *
 * - `/mis-datos`: sin token, «Entra en Mis datos» (2.9); con token, «Mis datos» (2.10). Al caducar, «Tu sesión ha
 *   caducado» y de nuevo el código.
 * - `/baja/{token}`, `/permiso/{token}` y `/copia/{token}`: los enlaces de un toque de los mensajes (2.11).
 *
 * - `/pagar/{business_id}/{link_code}`: la página de pago (4.9), anónima; Stripe vuelve con `?vuelta=1`.
 * - `/mis-pagos`: «Mis pagos» (4.10), con el mismo token de cliente y el mismo «Entra» que «Mis datos».
 * - `/mis-mensajes`: «Mis mensajes» (5.7), igual: lo que le ha mandado el negocio y pedirlo otra vez.
 * - `/mis-conversaciones`: «Mis conversaciones» (11.10), igual: lo hablado con el negocio por cualquier canal (solo leer).
 * - `/v1.<negocio>.<código>.<kid>.<firma>`: la página del pase (6.6), la dirección del QR, anónima.
 * - `/mis-pases`: «Mis pases» (6.7), con el mismo token de cliente y el mismo «Entra».
 * - `/firma/{business_id}/{token}`: firmar un documento por el enlace del mensaje (9.12), anónima; vuelve con `?vuelta=1`.
 * - `/mis-documentos`: «Mis documentos» (9.12), con el mismo token de cliente y el mismo «Entra».
 * - `/pedir-cita/{slug}` (la web) y `/pedir-cita?clave=pk_…&marco=widget` (el widget): pedir cita (9.9), con la marca del
 *   negocio; se identifica en el paso «Tus datos» con el mismo token de cliente.
 * - `/mis-citas`: «Mis citas» (9.10), con el mismo token y el mismo «Entra»; `/cita/{business_id}/{token}`: el enlace de la
 *   cita, anónimo.
 * - `/reservar/{slug}` (web) y `/reservar?clave=…&marco=widget` (widget): la reserva pública (8.11) y la estancia (8.9),
 *   con la marca del negocio; identificarse dentro guarda el mismo token de cliente.
 * - `/mis-reservas`: «Mis reservas» (8.12), con el mismo token de cliente y el mismo «Entra».
 * - `/comprar-entradas/{slug}` (web) y `/comprar-entradas?clave=…&marco=widget` (widget): la compra de entradas (10.13,
 *   G901), con la marca del negocio; identificarse dentro guarda el mismo token de cliente.
 *
 * 2.12 «Mi espacio» llega en la fase 3.
 */
export interface PropsZonaCliente {
    ruta: string;
    /** La consulta de la dirección (`?vuelta=1` de la página de pago). */
    busqueda?: URLSearchParams;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    /** Cliente de la API de la zona, con el token de cliente que se le dé (por defecto, el mismo origen). */
    crearApi?: CrearApi;
    /** Clave pública provisional de las páginas de Reservaria (`VITE_CLAVE_PUBLICA_CLIENTES`). */
    clavePublica?: string | null;
    captcha?: (accion: string) => Promise<string | null>;
    almacen?: AlmacenCliente;
    /** Espera del «Deshacer» (5 s; las pruebas la acortan). */
    esperaDeshacer?: number;
    ahora?: () => number;
    /** Abre la dirección temporal de la copia (por defecto, el navegador). */
    abrirDescarga?: (url: string) => void;
    /** Dónde guarda «Pedir cita» la cita pedida mientras se paga (por defecto, sessionStorage). */
    almacenCitas?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

type Estado = 'normal' | 'caducada' | 'borrada';

/** `/baja/abc` → `abc` (el token del enlace, tal cual). */
const tokenDe = (ruta: string, base: string): string => decodeURIComponent(ruta.slice(base.length + 1));

export function ZonaCliente({ ruta, busqueda, navegar, crearApi = crearApiPorDefecto, clavePublica, captcha = tokenCaptcha, almacen: dado, esperaDeshacer, ahora = Date.now, abrirDescarga, almacenCitas }: PropsZonaCliente) {
    const almacen = useMemo(() => dado ?? crearAlmacenCliente(), [dado]);
    const clave = clavePublica === undefined ? clavePublicaDelEntorno() : clavePublica;
    const ops = useMemo(() => operacionesDeLaZona(crearApi, almacen, clave), [crearApi, almacen, clave]);
    // «Mis entradas» (10.15) y «Salir de la lista» (10.16, G767): operaciones de Eventos y de Listas con el token de cliente.
    const entradas = useMemo(() => operacionesMisEntradas(crearApi(() => almacen.actual()?.token ?? null)), [crearApi, almacen]);
    // Reserva pública (8.11, 8.9) y «Mis reservas» (8.12): operaciones de Reservas con la clave del negocio o el token.
    const reservas = useMemo(() => operacionesMisReservas(crearApi(() => almacen.actual()?.token ?? null)), [crearApi, almacen]);
    // Compra de entradas (10.13, G901): operaciones de Eventos con la clave del negocio y, al comprar, el token.
    const compra = useMemo(() => operacionesCompraEntradas(crearApi(() => almacen.actual()?.token ?? null)), [crearApi, almacen]);
    const [sesion, setSesion] = useState<SesionCliente | null>(() => almacen.actual());
    const [estado, setEstado] = useState<Estado>('normal');
    // G922: dentro del marco de un widget, la altura va a `widget.js` (`reservaria:resize`).
    useAlturaEnMarco(esPaginaEnMarco(ruta, busqueda));

    const guardarToken = useCallback(
        (t: TokenCliente) => {
            const s = { token: t.access_token, caduca: t.expires_at };
            almacen.guardar(s);
            setSesion(s);
            setEstado('normal');
        },
        [almacen],
    );
    const olvidar = useCallback(
        (siguiente: Estado) => {
            almacen.guardar(null);
            setSesion(null);
            setEstado(siguiente);
        },
        [almacen],
    );
    const irAMisDatos = useCallback(() => {
        navegar(RUTAS_CLIENTE.misDatos);
    }, [navegar]);

    if (ruta.startsWith(`${RUTAS_CLIENTE.salirDeLaLista}/`)) {
        return <SalirDeLaLista ops={entradas} token={tokenDe(ruta, RUTAS_CLIENTE.salirDeLaLista)} busqueda={busqueda} />;
    }
    if (ruta.startsWith(`${RUTAS_CLIENTE.baja}/`)) {
        return <PaginaBaja ops={ops} token={tokenDe(ruta, RUTAS_CLIENTE.baja)} irAMisDatos={irAMisDatos} esperaDeshacer={esperaDeshacer} />;
    }
    if (ruta.startsWith(`${RUTAS_CLIENTE.permiso}/`)) {
        return <PaginaPermiso ops={ops} token={tokenDe(ruta, RUTAS_CLIENTE.permiso)} irAMisDatos={irAMisDatos} />;
    }
    if (ruta.startsWith(`${RUTAS_CLIENTE.copia}/`)) {
        return <PaginaCopia ops={ops} token={tokenDe(ruta, RUTAS_CLIENTE.copia)} irAMisDatos={irAMisDatos} abrir={abrirDescarga} />;
    }

    if (ruta.startsWith(`${RUTAS_CLIENTE.pagar}/`)) {
        const [negocioPago = '', codigo = ''] = ruta.slice(RUTAS_CLIENTE.pagar.length + 1).split('/').map((x) => decodeURIComponent(x));
        return <PaginaPago ops={ops} negocio={negocioPago} codigo={codigo} vuelta={busqueda?.get('vuelta') === '1'} navegar={navegar} ahora={ahora} />;
    }
    if (ruta.startsWith(`${RUTAS_CLIENTE.firmar}/`)) {
        const [negocioFirma = '', tokenFirma = ''] = ruta.slice(RUTAS_CLIENTE.firmar.length + 1).split('/').map((x) => decodeURIComponent(x));
        return <PaginaFirma ops={ops} negocio={negocioFirma} token={tokenFirma} vuelta={busqueda?.get('vuelta') === '1'} navegar={navegar} />;
    }
    if (ruta.startsWith(`${RUTAS_CLIENTE.cita}/`)) {
        const [negocioCita = '', tokenCita = ''] = ruta.slice(RUTAS_CLIENTE.cita.length + 1).split('/').map((x) => decodeURIComponent(x));
        return <EnlaceCita ops={ops} negocio={negocioCita} token={tokenCita} navegar={navegar} />;
    }
    if (ruta === RUTAS_CLIENTE.pedirCita || ruta.startsWith(`${RUTAS_CLIENTE.pedirCita}/`)) {
        const slug = decodeURIComponent(ruta.slice(RUTAS_CLIENTE.pedirCita.length + 1));
        const clavePedir = busqueda?.get('clave') ?? '';
        return (
            <PedirCita
                ops={ops}
                origen={slug === '' ? { tipo: 'widget', clave: clavePedir } : { tipo: 'web', slug }}
                busqueda={busqueda ?? new URLSearchParams()}
                navegar={navegar}
                identificado={sesion !== null && almacen.actual() !== null}
                captcha={captcha}
                onToken={guardarToken}
                onSalir={() => {
                    ops.revokeCustomerToken()
                        .catch(() => undefined)
                        .finally(() => { olvidar('normal'); });
                }}
                almacen={almacenCitas}
                ahora={ahora}
            />
        );
    }
    if (ruta === RUTAS_CLIENTE.comprarEntradas || ruta.startsWith(`${RUTAS_CLIENTE.comprarEntradas}/`)) {
        const slug = ruta === RUTAS_CLIENTE.comprarEntradas ? null : decodeURIComponent(ruta.slice(RUTAS_CLIENTE.comprarEntradas.length + 1));
        return <ComprarEntradas ops={ops} entradas={compra} slug={slug} busqueda={busqueda ?? new URLSearchParams()} captcha={captcha} onToken={guardarToken} conToken={sesion !== null && almacen.actual() !== null} navegar={navegar} ahora={ahora} almacen={almacenCitas} />;
    }
    if (ruta === RUTAS_CLIENTE.reservar || ruta.startsWith(`${RUTAS_CLIENTE.reservar}/`)) {
        const slug = ruta === RUTAS_CLIENTE.reservar ? null : decodeURIComponent(ruta.slice(RUTAS_CLIENTE.reservar.length + 1));
        return <ReservaPublica ops={ops} reservas={reservas} slug={slug} busqueda={busqueda ?? new URLSearchParams()} captcha={captcha} onToken={guardarToken} conToken={sesion !== null && almacen.actual() !== null} navegar={navegar} ahora={ahora} />;
    }
    if (esRutaPase(ruta)) {
        return <PaginaPase ops={ops} direccion={decodeURIComponent(ruta.slice(1))} navegar={navegar} />;
    }
    const enMisPases = ruta === RUTAS_CLIENTE.misPases;
    const enMisPagos = ruta === RUTAS_CLIENTE.misPagos;
    const enMisMensajes = ruta === RUTAS_CLIENTE.misMensajes;
    const enMisConversaciones = ruta === RUTAS_CLIENTE.misConversaciones;
    const enMisDocumentos = ruta === RUTAS_CLIENTE.misDocumentos;
    const enMisCitas = ruta === RUTAS_CLIENTE.misCitas;
    const enMisReservas = ruta === RUTAS_CLIENTE.misReservas;

    if (estado === 'borrada') {
        return (
            <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'cuenta' }} centrada>
                <section className="rv-pc-card" aria-labelledby="rv-pc-borrada-t">
                    <div className="rv-pc-baldosa-fila">
                        <Baldosa icono="trash-2" fondo="var(--rv-ink)" color="#fff" />
                        <Chip estado="pending">{D.enCurso}</Chip>
                    </div>
                    <div className="rv-pc-card__cab">
                        <h1 id="rv-pc-borrada-t">{T.t33.titulo}</h1>
                        <span>{T.t33.sub}</span>
                    </div>
                    <div className="rv-pc-lineas">
                        <div className="rv-pc-linea">
                            <Icono nombre="scale" />
                            <span>{T.t33.ley}</span>
                        </div>
                        <div className="rv-pc-linea">
                            <Icono nombre="user-plus" />
                            <span>{T.t33.nueva}</span>
                        </div>
                    </div>
                </section>
            </Pagina>
        );
    }

    const vigente = sesion !== null && almacen.actual() !== null;
    if (vigente && enMisPases) {
        return (
            <MisPases
                ops={ops}
                onCaducada={() => { olvidar('caducada'); }}
                onSalir={() => {
                    ops.revokeCustomerToken()
                        .catch(() => undefined)
                        .finally(() => { olvidar('normal'); });
                }}
            />
        );
    }
    if (vigente && enMisDocumentos) {
        return (
            <MisDocumentos
                ops={ops}
                navegar={navegar}
                negocio={busqueda?.get('negocio') ?? null}
                abrirDescarga={abrirDescarga}
                onCaducada={() => { olvidar('caducada'); }}
                onSalir={() => {
                    ops.revokeCustomerToken()
                        .catch(() => undefined)
                        .finally(() => { olvidar('normal'); });
                }}
            />
        );
    }
    if (vigente && enMisCitas) {
        return (
            <MisCitas
                ops={ops}
                busqueda={busqueda ?? new URLSearchParams()}
                navegar={navegar}
                onCaducada={() => { olvidar('caducada'); }}
                onSalir={() => {
                    ops.revokeCustomerToken()
                        .catch(() => undefined)
                        .finally(() => { olvidar('normal'); });
                }}
                ahora={ahora}
            />
        );
    }
    if (vigente && enMisConversaciones) {
        return (
            <MisConversaciones
                ops={ops}
                onCaducada={() => { olvidar('caducada'); }}
                onNoEsSuyo={() => { olvidar('normal'); }}
                onMisDatos={irAMisDatos}
                onSalir={() => {
                    ops.revokeCustomerToken()
                        .catch(() => undefined)
                        .finally(() => { olvidar('normal'); });
                }}
            />
        );
    }
    if (vigente && enMisMensajes) {
        return (
            <MisMensajes
                ops={ops}
                onCaducada={() => { olvidar('caducada'); }}
                onCambiar={irAMisDatos}
                onSalir={() => {
                    ops.revokeCustomerToken()
                        .catch(() => undefined)
                        .finally(() => { olvidar('normal'); });
                }}
            />
        );
    }
    if (vigente && enMisReservas) {
        return (
            <MisReservas
                ops={ops}
                reservas={reservas}
                navegar={navegar}
                onCaducada={() => { olvidar('caducada'); }}
                onSalir={() => {
                    ops.revokeCustomerToken()
                        .catch(() => undefined)
                        .finally(() => { olvidar('normal'); });
                }}
            />
        );
    }
    if (vigente && ruta === RUTAS_CLIENTE.misEntradas) {
        return (
            <MisEntradas
                ops={ops}
                entradas={entradas}
                idioma={idiomaInicial(busqueda, typeof navigator === 'undefined' ? undefined : navigator.language)}
                onCaducada={() => { olvidar('caducada'); }}
                onSalir={() => {
                    ops.revokeCustomerToken()
                        .catch(() => undefined)
                        .finally(() => { olvidar('normal'); });
                }}
            />
        );
    }
    if (vigente && enMisPagos) {
        return (
            <MisPagos
                ops={ops}
                onCaducada={() => { olvidar('caducada'); }}
                onSalir={() => {
                    ops.revokeCustomerToken()
                        .catch(() => undefined)
                        .finally(() => { olvidar('normal'); });
                }}
            />
        );
    }
    if (vigente) {
        return (
            <MisDatos
                ops={ops}
                captcha={captcha}
                onToken={guardarToken}
                onCaducada={() => { olvidar('caducada'); }}
                onSalir={() => {
                    // «Salir»: se revoca el token en la API (si no se puede, se olvida igual).
                    ops.revokeCustomerToken()
                        .catch(() => undefined)
                        .finally(() => { olvidar('normal'); });
                }}
                onCuentaBorrada={() => { olvidar('borrada'); }}
                esperaDeshacer={esperaDeshacer}
                ahora={ahora}
            />
        );
    }

    return (
        <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'cuenta' }} centrada>
            <Identificarse key={estado} ops={ops} contexto={{ tipo: estado === 'caducada' || sesion !== null ? 'sesion-caducada' : enMisPases ? 'mis-pases' : enMisPagos ? 'mis-pagos' : enMisMensajes ? 'mis-mensajes' : enMisConversaciones ? 'mis-conversaciones' : enMisCitas ? 'mis-citas' : 'mis-datos' }} captcha={captcha} onToken={guardarToken} negocioDelCodigo={enMisDocumentos && /^biz_[0-9A-HJKMNP-TV-Z]{26}$/.test(busqueda?.get('negocio') ?? '') ? (busqueda?.get('negocio') ?? null) : null} ahora={ahora} />
        </Pagina>
    );
}
